/**
 * Buyer-language layer (SYN-1486, promoted to the canonical dictionary by
 * SYN-1502) — the SINGLE module that converts every internal system term into
 * buyer language before it can reach a buyer-visible surface. Translate,
 * never echo.
 *
 * Invariants (SYN-1485 epic):
 * - Translation changes WORDS, never VERDICTS — callers pass derived state
 *   through unchanged; this module only chooses how it reads.
 * - No output may contain a raw enum (snake_case token), a capability ID
 *   (`cap.<domain>.<verb>`), a UUID-shaped string, or any term in
 *   `FORBIDDEN_PRIMARY_TERMS` (engine names included).
 * - Unknown/future inputs fall back to safe GENERIC copy — the raw key is
 *   never rendered as the fallback (no title-casing of unknown tokens).
 * - Technical refs stay reachable behind the collapsed "Technical details"
 *   affordance (see `TechnicalDetails` component), which is the ONLY place
 *   raw identifiers may render (`data-technical-details`).
 *
 * Every buyer-visible consumer (marketplace cards, readiness lists, setup
 * actions, quick-start, admin surfaces rendered to org admins) sources its
 * copy from here so nothing drifts; `test/vocabulary-leakage.ts` enforces the
 * invariants on rendered output, and `test/page-vocabulary-guard.test.tsx`
 * sweeps the primary copy of every route (SYN-1502).
 */

export const TECHNICAL_DETAILS_LABEL = "Technical details";

// ---------------------------------------------------------------------------
// Canonical dictionary (SYN-1502)
// ---------------------------------------------------------------------------

/**
 * Canonical system→product vocabulary. This is the dictionary as CODE, not
 * convention: when a surface needs the product word for a system concept, it
 * reads it from here. Both the current IA and the new buyer IA share this one
 * dictionary.
 */
export const BUYER_DICTIONARY = {
  /**
   * ACSS contract object. Reads as "routine" or stays "contract" depending on
   * a derived tier — tier derivation itself is later work, so consumers that
   * cannot derive yet keep "contract".
   */
  contract: "routine",
  marketplace: "catalog",
  listing: "catalog",
  capability: "permission",
  grant: "access",
  "grant request": "access request",
  bundle: "connection",
  "registered API": "connection",
  "API binding": "connection",
  readiness: "setup status",
  execution: "run",
  /** Or "agent" when the principal is not a human. */
  principal: "person",
  escalation: "approval",
  "two-person confirmation": "approval",
  /**
   * Admin grouping term. User-facing label is "Toolkit" (SYN-1899). The
   * customize package surfaces (SYN-1901) consume this and drop the opaque
   * item count, which users cannot act on.
   */
  package: "Toolkit",
  /** Already user-facing — translates to itself. */
  credits: "credits",
  /**
   * Chat-drawer "tools" section — was "Capabilities", which collides with
   * governance/connection API senses (SYN-1899). The rendered chat surface
   * (`components/chat/OperationTimeline`) and `DRAWER_SECTIONS` both read
   * "Tools".
   */
  "Capability (chat drawer)": "Tools",
} as const satisfies Record<string, string>;

/**
 * Terms that are NEVER primary copy: they may only appear inside the
 * `TechnicalDetails` expander (`data-technical-details`).
 */
export const TECHNICAL_ONLY_TERMS = ["canary", "evidence kind", "drift"] as const;

/**
 * Terms forbidden in buyer-visible primary copy (SYN-1502): plumbing words,
 * identity-plane jargon, and engine names. `cap.*` identifiers and
 * UUID-shaped labels are forbidden too but matched structurally by
 * `test/vocabulary-leakage.ts`. The SOLE sanctioned exception is content
 * inside `TechnicalDetails` (`data-technical-details`).
 */
export const FORBIDDEN_PRIMARY_TERMS = [
  "DAG",
  "schema",
  "primitive",
  "canary",
  "drift",
  "principal",
  "OBO",
  "ACSS",
  "Atlas",
  "Coherence",
  "ISAAC",
  "Phoenix",
  "Tempo",
] as const;

/**
 * Word-boundary regex source matching any forbidden primary term, including
 * simple plurals ("schemas", "canaries"). Case-insensitive when compiled.
 * Shared with the leakage detector so the dictionary stays the single source.
 */
export const FORBIDDEN_PRIMARY_TERM_PATTERN = `\\b(?:${FORBIDDEN_PRIMARY_TERMS.map((term) =>
  term === "canary" ? "canar(?:y|ies)" : `${term}s?`,
).join("|")})\\b`;

const FORBIDDEN_PRIMARY_RE = new RegExp(FORBIDDEN_PRIMARY_TERM_PATTERN, "i");

/** True when text carries a term that must never reach buyer primary copy. */
export function containsForbiddenPrimaryTerm(text: string): boolean {
  return FORBIDDEN_PRIMARY_RE.test(text);
}

// ---------------------------------------------------------------------------
// Setup status (SYN-1502 dictionary: readiness → "setup status")
// ---------------------------------------------------------------------------

/** The four buyer-facing setup statuses. */
export type SetupStatus = "Ready" | "Needs setup" | "Waiting" | "Problem";

const SETUP_STATUS_BY_STATE: Record<string, SetupStatus> = {
  ready: "Ready",
  needs_setup: "Needs setup",
  stale: "Waiting",
  unknown: "Waiting",
  blocked: "Problem",
  unavailable: "Problem",
};

/**
 * Buyer setup-status label for a derived readiness state. Fail-closed:
 * unrecognized states read as "Waiting", never as "Ready".
 */
export function setupStatusLabel(state: string): SetupStatus {
  return SETUP_STATUS_BY_STATE[state] ?? "Waiting";
}

// ---------------------------------------------------------------------------
// Setup actions
// ---------------------------------------------------------------------------

export interface SetupActionCopy {
  /** Short human title for the step (card/list heading). */
  title: string;
  /** One-sentence explanation of what it means and why. */
  explanation: string;
  /** CTA label, imperative buyer language. */
  cta: string;
  /**
   * Actionable CTA for org admins when the default `cta` is the non-admin
   * "ask your admin" phrasing (SYN-1487). Omitted when `cta` is already
   * actionable for admins.
   */
  adminCta?: string;
}

/** Non-admin CTA for actions only an administrator can perform (SYN-1487). */
export const ASK_YOUR_ADMIN_CTA = "Ask your admin";

/** Safe fallback for unknown/future action types — never echoes the raw key. */
export const GENERIC_SETUP_ACTION: SetupActionCopy = {
  title: "One more setup step is needed",
  explanation: "Finish this step before this can run. Your admin can see the specifics.",
  cta: "View step",
};

const SETUP_ACTION_COPY: Record<string, SetupActionCopy> = {
  assign_role: {
    title: "Team access needed",
    explanation: "Someone on your team needs to be given access before this can run.",
    cta: "Set team access",
  },
  request_role: {
    title: "Request access",
    explanation: "You can request the access this needs from your admin.",
    cta: "Request access",
  },
  issue_grant: {
    title: "Needs admin approval",
    explanation: "An administrator needs to approve access before this can run.",
    cta: "Ask your admin",
    adminCta: "Approve access",
  },
  connect_connector: {
    title: "Connect your account",
    explanation: "Link the account this works with so it can act on your behalf.",
    cta: "Connect your account",
  },
  register_capability: {
    title: "Finish connecting this feature",
    explanation: "A connected app still needs one setup step completed.",
    cta: "Finish setup",
  },
  run_canary: {
    title: "Test this contract",
    explanation: "Run a quick safe test to confirm everything is wired up.",
    cta: "Test this contract",
  },
  resolve_drift: {
    title: "Settings need a review",
    explanation:
      "Something changed since this was last verified — review to bring it back in line.",
    cta: "Review settings",
  },
  grant_entitlement: {
    title: "Not included in your plan yet",
    explanation:
      "Your current plan doesn't include this — upgrading or adjusting the plan unlocks it.",
    cta: "Review plan",
  },
  refresh_readiness: {
    title: "Check again",
    explanation: "Re-check the latest status to pick up recent changes.",
    cta: "Check again",
  },
  // Defensive aliases used by earlier UX copy/tickets.
  connect_account: {
    title: "Connect your account",
    explanation: "Link the account this works with so it can act on your behalf.",
    cta: "Connect your account",
  },
  request_grant: {
    title: "Needs admin approval",
    explanation: "An administrator needs to approve access before this can run.",
    cta: "Ask your admin",
    adminCta: "Approve access",
  },
  view_evidence: {
    title: "See what was checked",
    explanation: "Review the checks behind this status.",
    cta: "See checks",
  },
  // Community grab (SYN-1899): self-service adoption of community-scoped items.
  grab: {
    title: "Add to your workspace",
    explanation: "This is available to everyone — add it to start using it.",
    cta: "Add to workspace",
  },
};

/**
 * Buyer copy for a setup-action type. `context.system` personalizes
 * account-connection copy ("Connect your Xero account").
 */
export function setupActionCopy(
  actionType: string | null | undefined,
  context?: { system?: string },
): SetupActionCopy {
  const copy = actionType ? SETUP_ACTION_COPY[actionType] : undefined;
  if (!copy) {
    return GENERIC_SETUP_ACTION;
  }
  if (context?.system && (actionType === "connect_connector" || actionType === "connect_account")) {
    const cta = `Connect your ${context.system} account`;
    return { ...copy, title: cta, cta };
  }
  return copy;
}

// ---------------------------------------------------------------------------
// Readiness states
// ---------------------------------------------------------------------------

export interface ReadinessStateCopy {
  label: string;
  explanation: string;
}

const READINESS_STATE_COPY: Record<string, ReadinessStateCopy> = {
  ready: {
    label: "Ready to run",
    explanation: "Everything this needs is in place.",
  },
  needs_setup: {
    label: "Needs setup",
    explanation: "A quick setup step is needed before this can run.",
  },
  blocked: {
    label: "Blocked",
    explanation: "Something is preventing this from running right now.",
  },
  stale: {
    label: "Needs a re-check",
    explanation: "It's been a while since this was checked — check again before relying on it.",
  },
  unavailable: {
    label: "Not available",
    explanation: "This isn't available in your workspace.",
  },
  unknown: {
    label: "Can't confirm yet",
    explanation: "We couldn't confirm this is ready. Try checking again.",
  },
};

/**
 * Buyer copy for a readiness state. Unknown values read as the fail-closed
 * "unknown" copy — the verdict itself is the caller's, never invented here.
 */
export function readinessStateCopy(state: string): ReadinessStateCopy {
  return READINESS_STATE_COPY[state] ?? READINESS_STATE_COPY.unknown;
}

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

const EVIDENCE_KIND_LABEL: Record<string, string> = {
  catalog: "Catalog",
  grant: "Approval",
  role: "Team access",
  connector: "Account connection",
  registration: "App setup",
  canary: "Verification test",
  drift: "Settings check",
  dependency: "Required service",
  entitlement: "Plan access",
  stale: "Freshness",
};

const GENERIC_EVIDENCE_KIND = "Setup check";

/** Buyer label for an evidence/blocker kind; generic for unknown kinds. */
export function evidenceKindLabel(kind: string): string {
  return EVIDENCE_KIND_LABEL[kind] ?? GENERIC_EVIDENCE_KIND;
}

const EVIDENCE_STATUS_LABEL: Record<string, string> = {
  satisfied: "In place",
  needs_setup: "Needs setup",
  blocked: "Blocked",
  stale: "Needs a re-check",
  unknown: "Unconfirmed",
  not_required: "Not needed",
};

/** Buyer label for an evidence status; fail-closed generic for unknowns. */
export function evidenceStatusLabel(status: string): string {
  return EVIDENCE_STATUS_LABEL[status] ?? "Unconfirmed";
}

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

/** Known capability domains → buyer nouns. Engine names never escape. */
const CAPABILITY_DOMAIN_NAME: Record<string, string> = {
  atlas: "platform",
  calendar: "calendar",
  contract: "contracts",
  coherence_lifecycle: "connected apps",
  finops: "billing and usage",
  observability: "activity and monitoring",
  sales: "sales data",
  memory: "workspace memory",
  identity: "team access",
};

/** Known capability verbs → buyer verbs. */
const CAPABILITY_VERB_NAME: Record<string, string> = {
  read: "View",
  write: "Update",
  execute: "Run",
  preview: "Preview",
  manage: "Manage",
  admin: "Administer",
};

const GENERIC_CAPABILITY = "App permission";

function humanizeToken(token: string): string {
  return token.split(/[_-]+/).filter(Boolean).join(" ");
}

/**
 * Human capability name for a `cap.<domain>.<verb>` ID. Known domains/verbs
 * get curated buyer phrasing; well-formed unknown IDs are humanized into
 * plain words (no `cap.`, dots, or underscores ever escape); malformed input
 * — or a humanization that would still carry a forbidden term ("principal",
 * an engine name) — reads as a generic permission (SYN-1502, fail-closed).
 */
export function capabilityName(capabilityId: string): string {
  const match = /^cap\.([a-z0-9_-]+)\.([a-z0-9_-]+)$/i.exec(capabilityId ?? "");
  if (!match) {
    return GENERIC_CAPABILITY;
  }
  const [, rawDomain, rawVerb] = match;
  const domain = CAPABILITY_DOMAIN_NAME[rawDomain] ?? humanizeToken(rawDomain);
  const verb = CAPABILITY_VERB_NAME[rawVerb] ?? null;
  const humanVerb = humanizeToken(rawVerb);
  const name = verb
    ? `${verb} ${domain}`
    : `${humanVerb.charAt(0).toUpperCase()}${humanVerb.slice(1)} ${domain}`;
  return containsForbiddenPrimaryTerm(name) ? GENERIC_CAPABILITY : name;
}

// ---------------------------------------------------------------------------
// Readiness reason summaries + data-label scrubbing (SYN-1502 — leaks found
// verifying the running app: Atlas populates `reasonSummary` with machine
// codes, and gateway-registered skill/package names can carry engine names)
// ---------------------------------------------------------------------------

/** Curated buyer phrasing for known machine readiness-reason codes. */
const READINESS_REASON_LABELS: Record<string, string> = {
  snapshot_past_freshness_horizon: "Setup info is out of date and needs a re-check.",
};

/** Machine-identifier shape: snake_case code, cap.* id, or UUID — not prose. */
const MACHINE_LABEL_RE =
  /^(?:cap\.[a-z0-9_.-]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[a-z0-9]+(?:_[a-z0-9]+)+)$/i;

/**
 * Buyer-safe readiness reason. Known machine codes get curated copy; unknown
 * machine-shaped strings and forbidden-term prose fail closed to `undefined`
 * so callers fall back to their next description source.
 */
export function readinessReasonLabel(reason: string | undefined): string | undefined {
  const trimmed = reason?.trim();
  if (!trimmed) {
    return undefined;
  }
  const curated = READINESS_REASON_LABELS[trimmed.toLowerCase()];
  if (curated) {
    return curated;
  }
  if (MACHINE_LABEL_RE.test(trimmed) || containsForbiddenPrimaryTerm(trimmed)) {
    return undefined;
  }
  return trimmed;
}

// ---------------------------------------------------------------------------
// Connection refusals (SYN-2712)
// ---------------------------------------------------------------------------

/**
 * Curated buyer phrasing for the reasons a connect attempt can be refused.
 *
 * 🔴 WHY THIS EXISTS. On 30 Aug a customer clicked Connect on Microsoft 365,
 * a browser flashed, and chat said NOTHING. The gateway had failed on a
 * configuration problem it could not recover from, and the failure never
 * reached a sentence. The gateway now refuses out loud and names the cause,
 * but its causes are machine codes: `clerk_oauth_not_configured`,
 * `client_id_unavailable`, `callback_uri_not_allowed`. Rendering those raw
 * would trade silence for jargon, and it would breach this module's own
 * invariant that no snake_case token reaches buyer copy.
 *
 * Two audiences, and the split is deliberate. Most of these are OUR problem,
 * not the customer's: there is nothing Randy can do about a missing key, so
 * the copy says so and does not invite him to retry forever. The few he can
 * act on say what HE should do.
 *
 * 🚩 NOT ONE OF THESE LINES MAY SAY WE HAVE BEEN TOLD. An earlier draft ended
 * ten of them with exactly that, and it is false: nothing alarms on any of
 * these. epic-015's A1 is DEFINED, NOT BUILT, a gateway ERROR log is not a
 * notification, and the whole premise of that epic is that 39 to 43 hours
 * passed before we knew. Telling the customer we already know is worse than
 * saying nothing, because it removes his reason to tell us, and he is
 * currently the only alarm that works. Restore the claim only when something
 * actually fires. Until then, ask him to tell us.
 */
const CONNECTION_REFUSAL_LABELS: Record<string, string> = {
  // Ours to fix. He cannot act on these, so say so and ask him to report it.
  clerk_oauth_not_configured:
    "This connection is not finished being set up on our side yet, and nothing you do here will complete it. Please tell us you hit this.",
  client_id_unavailable:
    "This connection is not finished being set up on our side yet. Please tell us you hit this.",
  authorization_endpoint_unknown:
    "This connection is not finished being set up on our side yet. Please tell us you hit this.",
  callback_uri_required:
    "This connection is not finished being set up on our side yet. Please tell us you hit this.",
  callback_uri_not_allowed:
    "This connection tried to send you somewhere we do not recognise, so we stopped it. Please tell us you hit this.",
  auth_method_unsupported: "We cannot sign you in to this system yet. Please tell us you hit this.",
  clerk_provider_unknown: "We cannot sign you in to this system yet. Please tell us you hit this.",
  persistence_unavailable:
    "Something on our side is not responding, so we stopped rather than half-connect you. Try again in a few minutes.",
  authorization_store_unavailable:
    "Something on our side is not responding, so we stopped rather than half-connect you. Try again in a few minutes.",
  authorization_start_blocked: "We could not start connecting this system.",
  oauth_start_unavailable: "We could not start connecting this system.",
  certification_id_required:
    "This connection needs to be checked before you can sign in to it. Please tell us you hit this.",
  governed_oauth_authority_fields_forbidden:
    "That request was not put together the way we expect, so we stopped it. Please tell us you hit this.",
  connector_blocked: "This system cannot be connected right now.",

  // Theirs to act on.
  connector_disabled:
    "This system has been turned off for your company. An admin can turn it back on.",
  connection_not_found: "We could not find that system. Try picking it from the list again.",
  credential_deactivated:
    "The sign-in saved for this system was switched off. Connect it again to carry on.",
  missing_binding: "You do not have access to this system yet. Ask your admin.",
  no_connector_account: "Nobody has connected this system yet. Connect it to carry on.",
  already_connected: "This system is already connected.",

  // SYN-2879. THE WRONG ACCOUNT, and the reason this pair reads the way it
  // does. On 5 Sep a customer signed in, was told "connected", and every
  // question failed: the account he had used has no mailbox. Neither code had
  // a line here, so the model wrote its own and told him the connection was
  // refused, that admin consent was already completed, and to contact support.
  // None of that was true and none of it was his to fix, while the thing that
  // was took under a minute.
  //
  // 🚩 So these two must never mention approval, permissions, an admin or us.
  // The whole content of the sentence is: you are signed in as somebody who
  // cannot do this, sign in as yourself instead. `connection-refusal-language.test.ts`
  // holds that line.
  no_capability_available:
    "You are signed in, but that account cannot reach anything we need. Connect this system again and sign in with the account your own work is in.",
  mailbox_not_available:
    "That account has no mailbox, so there is no mail here to read. Connect this system again and sign in with the account your own email arrives in.",
};

/** Said when we have no curated line. Never the raw code, never nothing. */
const GENERIC_CONNECTION_REFUSAL = "We could not connect this system. Please tell us you hit this.";

/**
 * Buyer-safe sentence for a refused connect attempt.
 *
 * Unlike {@link readinessReasonLabel} this NEVER fails closed to `undefined`.
 * There, an absent label lets the caller fall back to a better description it
 * already holds. Here the caller's fallback is the string "Action failed",
 * which is the defect this function exists to remove. So an unknown code
 * yields generic copy, and the raw token is dropped rather than rendered.
 *
 * Reasons may carry a colon-suffixed detail
 * (`governed_oauth_authority_fields_forbidden:connection_id`); the prefix
 * decides the copy and the suffix is discarded from buyer view. It stays
 * available to `TechnicalDetails`, the one sanctioned home for raw ids.
 */
export function connectionRefusalLabel(reason: string | null | undefined): string {
  const trimmed = reason?.trim();
  if (!trimmed) {
    return GENERIC_CONNECTION_REFUSAL;
  }
  const key = trimmed.split(":", 1)[0].toLowerCase();
  const curated = CONNECTION_REFUSAL_LABELS[key];
  if (curated) {
    return curated;
  }
  // Prose from upstream is allowed through only if it is clean on both counts.
  if (!MACHINE_LABEL_RE.test(trimmed) && !containsForbiddenPrimaryTerm(trimmed)) {
    return trimmed;
  }
  return GENERIC_CONNECTION_REFUSAL;
}

const FORBIDDEN_PRIMARY_RE_GLOBAL = new RegExp(FORBIDDEN_PRIMARY_TERM_PATTERN, "gi");

/**
 * Strip forbidden terms (engine names, spec jargon) from data-supplied labels
 * — skill names, package ids — before they reach buyer primary copy. Falls
 * back to a generic label when scrubbing would leave nothing (fail-closed).
 *
 * Separators are normalized to spaces FIRST: the forbidden-term pattern is
 * `\b`-bounded and `_` is a word char, so without this `atlas_contracts` or
 * `coherence_bundle` would slip through the engine-name scrub and render the
 * raw snake_case token in buyer copy (verified leak, SYN-1502 follow-up).
 */
export function scrubEngineNames(label: string | undefined, fallback = "Platform"): string {
  const scrubbed = (label ?? "")
    .replace(/[-_]+/g, " ")
    .replace(FORBIDDEN_PRIMARY_RE_GLOBAL, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  return scrubbed.length > 0 ? scrubbed : fallback;
}

// ---------------------------------------------------------------------------
// Quick-start (SYN-1480 panel; copy owned here per SYN-1486 so the activation
// panel and the cards it points at speak identically — SYN-1490 deep-links)
// ---------------------------------------------------------------------------

export interface QuickStartStepCopy {
  title: string;
  detailDone: string;
  detailTodo: string;
  actionLabel: string;
  /** CTA when the step's target exists but is not yet verified (SYN-1490). */
  actionLabelUnverified?: string;
}

export const QUICK_START_STEP_COPY: Record<
  | "plan_credits"
  | "connect_auth"
  | "ask_first_question"
  | "starter_contract"
  | "roles_invites"
  | "first_run",
  QuickStartStepCopy
> = {
  plan_credits: {
    title: "Plan & credits active",
    detailDone: "Your subscription is active with credits available.",
    detailTodo: "Confirm your plan is active and credits are available.",
    actionLabel: "Review plan & credits",
  },
  // 005.044 AC4: the OUTCOME, not the step. "Connect a system" is our plumbing
  // showing on his screen; he came to ask his business a question.
  ask_first_question: {
    title: "Ask your first question",
    detailDone: "You can ask your connected systems questions in plain English.",
    detailTodo: "Connect a system you already use and ask it something in plain English.",
    actionLabel: "Ask a question",
  },
  connect_auth: {
    title: "Connect required access",
    detailDone: "All the accounts and approvals this workspace needs are connected.",
    detailTodo: "Connect the accounts and approvals your workspace still needs.",
    actionLabel: "Open connections",
  },
  starter_contract: {
    title: "Pick a starter contract",
    detailDone: "Your starter contracts are live in the marketplace.",
    detailTodo: "Browse the starter contracts set up for your organization.",
    actionLabel: "Browse marketplace",
  },
  roles_invites: {
    title: "Set roles & invite your team",
    detailDone: "No open team-access gaps for your organization.",
    detailTodo: "Set team access and invite teammates.",
    actionLabel: "Manage team access",
  },
  first_run: {
    title: "Run your first contract",
    detailDone: "A contract is verified and ready to run.",
    detailTodo: "Run a starter contract to see the path work end to end.",
    actionLabel: "Run a contract",
    actionLabelUnverified: "Test a contract",
  },
};

// ---------------------------------------------------------------------------
// Subjects + display names
// ---------------------------------------------------------------------------

const SUBJECT_TYPE_LABEL: Record<string, string> = {
  org: "Your organization",
  role: "Team role",
  user: "You",
};

/** Buyer label for a readiness subject type. */
export function subjectTypeLabel(type: string): string {
  return SUBJECT_TYPE_LABEL[type] ?? "Your workspace";
}

/**
 * Buyer label for an evidence/blocker item ref: capability IDs get their
 * human capability name, UUID-shaped refs and empties read as a generic
 * noun, snake_case keys are humanized into plain words. No raw shape escapes.
 */
export function evidenceItemLabel(label: string | null | undefined): string {
  const trimmed = label?.trim();
  if (!trimmed) {
    return "Setup item";
  }
  // Requirement keys arrive kind-prefixed ("capability:cap.contract.execute",
  // "connector:gmail") — strip the prefix and translate the payload.
  const prefixed = /^([a-z][a-z0-9_-]*):(.+)$/i.exec(trimmed);
  if (prefixed) {
    return evidenceItemLabel(prefixed[2]);
  }
  // Item labels sometimes fall back to the evidence kind itself ("canary") —
  // translate those through the kind map before anything else.
  if (EVIDENCE_KIND_LABEL[trimmed.toLowerCase()]) {
    return EVIDENCE_KIND_LABEL[trimmed.toLowerCase()];
  }
  if (/^cap\./i.test(trimmed)) {
    return capabilityName(trimmed);
  }
  if (UUID_SHAPED.test(trimmed)) {
    return "This contract";
  }
  if (/[_]/.test(trimmed) && !/\s/.test(trimmed)) {
    const human = humanizeToken(trimmed);
    const label = human.charAt(0).toUpperCase() + human.slice(1);
    // Humanizing must not surface a forbidden term ("policy_canary").
    return containsForbiddenPrimaryTerm(label) ? "Setup item" : label;
  }
  // Sentence labels can EMBED leak shapes ("Capability cap.contract.execute
  // (org-scoped grant)") — sanitize each occurrence in place.
  const sanitized = trimmed
    .replace(/\bcap\.[a-z0-9_-]+\.[a-z0-9_-]+\b/gi, (id) => capabilityName(id))
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "this contract")
    .replace(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g, (token) => humanizeToken(token));
  // Sentences still carrying a forbidden term (engine names, plumbing words —
  // SYN-1502) fail closed to the generic noun; the raw label stays reachable
  // in Technical details.
  return containsForbiddenPrimaryTerm(sanitized) ? "Setup item" : sanitized;
}

/**
 * Buyer-safe status reason: Atlas state reasons can be plain sentences OR raw
 * enum tokens ("requirement_unknown"). Single snake_case tokens humanize;
 * anything still carrying a leak shape (cap.* / UUID / embedded enums) reads
 * as the caller's generic; plain sentences pass through.
 */
export function buyerStatusReason(reason: string | null | undefined, generic: string): string {
  const trimmed = reason?.trim();
  if (!trimmed) {
    return generic;
  }
  if (/^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/i.test(trimmed)) {
    const human = humanizeToken(trimmed);
    return human.charAt(0).toUpperCase() + human.slice(1);
  }
  if (
    /\bcap\.[a-z0-9_-]+\.[a-z0-9_-]+\b/i.test(trimmed) ||
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(trimmed) ||
    /\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/.test(trimmed) ||
    containsForbiddenPrimaryTerm(trimmed)
  ) {
    return generic;
  }
  return trimmed;
}

/**
 * Buyer label for a raw facet/filter VALUE (SYN-1502 — marketplace facet
 * dropdowns): connector refs read as system names ("gmail" → "Gmail"),
 * capability IDs translate, snake_case values humanize. Raw values stay the
 * filter KEYS; only the rendered label translates.
 */
export function facetLabel(value: string): string {
  return systemNameFromRef(value) ?? evidenceItemLabel(value);
}

/**
 * Derive a presentable system name from a connector target ref ("gmail" →
 * "Gmail"). Returns undefined unless the ref is a single clean token — refs
 * carrying separators, ids, or enum shapes never reach buyer copy.
 */
export function systemNameFromRef(ref: string | null | undefined): string | undefined {
  const trimmed = ref?.trim();
  if (!trimmed || !/^[a-z][a-z0-9]*$/i.test(trimmed)) {
    return undefined;
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

const UUID_SHAPED = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ---------------------------------------------------------------------------
// Trust + risk plain-English labels (SYN-1558)
// ---------------------------------------------------------------------------

const TRUST_LABEL: Record<string, string> = {
  silent: "Runs on its own",
  notify_after: "Runs, then tells you what it did",
  require_confirmation: "Asks you before it acts",
  require_two_person: "Needs approval from two people",
};

/**
 * Plain-English "how it runs" label for a trustDefault value. Unknown/empty
 * values return undefined so the caller can omit the line entirely (fail-safe).
 */
export function trustLabel(trustDefault: string | null | undefined): string | undefined {
  return trustDefault ? (TRUST_LABEL[trustDefault] ?? undefined) : undefined;
}

const RISK_LABEL: Record<string, string> = {
  medium: "Standard impact",
  high: "Higher impact - review before running",
};

/**
 * Plain-English risk label. Returns null for "low" (no label shown — only
 * elevated risk is surfaced to buyers) and undefined for unknown values.
 */
export function riskLabel(riskClass: string | null | undefined): string | null | undefined {
  if (!riskClass) {
    return undefined;
  }
  if (riskClass === "low") {
    return null;
  }
  return RISK_LABEL[riskClass] ?? undefined;
}

// ---------------------------------------------------------------------------
// Display name
// ---------------------------------------------------------------------------

/**
 * Display name that is never a UUID: contract/listing UUIDs are not labels —
 * the name is. When the only candidate is UUID-shaped (or empty), the generic
 * noun renders instead and the raw ref stays in Technical details.
 */
export function displayName(
  candidate: string | null | undefined,
  generic: string = "This contract",
): string {
  const trimmed = candidate?.trim();
  if (!trimmed || UUID_SHAPED.test(trimmed)) {
    return generic;
  }
  return trimmed;
}
